import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getProduct, timeSlots, weekdays } from "../data";
import { useApp } from "../store";
import { getSchedule } from "../services/firestore";
import "../css/Subscription.css";
import {
  addDays,
  isDatePaused,
  nextDeliveryDate,
  toDateKey,
  isOrderModifiable,
} from "../utils/admin";
import { PageHeader, PrimaryButton, StatusBadge } from "../components/UI";

function getNextCalendarWeekRange(from = new Date()) {
  const current = new Date(from);
  current.setHours(0, 0, 0, 0);

  const day = current.getDay();
  const daysUntilNextMonday = day === 0 ? 1 : 8 - day;
  const start = addDays(current, daysUntilNextMonday);
  const end = addDays(start, 6);

  return {
    from: toDateKey(start),
    until: toDateKey(end),
  };
}

function getNextMonthRange(from = new Date()) {
  const current = new Date(from);
  const start = new Date(current.getFullYear(), current.getMonth() + 1, 1);
  const end = new Date(current.getFullYear(), current.getMonth() + 2, 0);

  return {
    from: toDateKey(start),
    until: toDateKey(end),
  };
}

export function Subscription() {
  const { id } = useParams();
  const {
    deliveries,
    setDeliveries,
    updateSchedule,
    updateOrdersForSchedule: updateOrdersForScheduleInStore,
    updateOrderStatus,
  } = useApp();

  const delivery = deliveries.find((item) => item.id === id);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pauseMode, setPauseMode] = useState("day");
  const [pauseDate, setPauseDate] = useState("");
  const [pauseRange, setPauseRange] = useState({ from: "", until: "" });

  useEffect(() => {
    let mounted = true;

    async function loadSchedule() {
      if (!delivery?.scheduleId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const result = await getSchedule(delivery.scheduleId);
        if (mounted) setSchedule(result);
      } catch (err) {
        if (mounted) {
          setError(err?.message || "Unable to load your schedule.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSchedule();

    return () => {
      mounted = false;
    };
  }, [delivery?.scheduleId]);

  const product = useMemo(
    () => getProduct(schedule?.productId || delivery?.productId),
    [schedule?.productId, delivery?.productId],
  );

  const currentSchedule = schedule || delivery;
  const nextDate = currentSchedule
    ? nextDeliveryDate(currentSchedule)
    : delivery?.deliveryDate;

  const pausedToday = Boolean(
    currentSchedule && isDatePaused(currentSchedule, new Date()),
  );

  const isCancelled = currentSchedule?.status === "cancelled";

  const displayDays = currentSchedule?.days?.length
    ? currentSchedule.days.join(", ")
    : "One time";

  const markLocalOrders = (from, until, status) => {
    setDeliveries((previous) =>
      previous.map((item) => {
        if (item.scheduleId !== delivery?.scheduleId) return item;

        const itemDate = item.deliveryDate;
        if (!itemDate) return item;
        if (from && itemDate < from) return item;
        if (until && itemDate > until) return item;
        if (["Delivered", "Cancelled", "Skipped"].includes(item.status)) {
          return item;
        }

        return {
          ...item,
          status,
        };
      }),
    );
  };

  const savePause = async () => {
    if (!delivery?.scheduleId) return;

    let from = "";
    let until = "";
    let pauseDates = Array.isArray(schedule?.pauseDates)
      ? [...schedule.pauseDates]
      : [];

    let pauseRanges = Array.isArray(schedule?.pauseRanges)
      ? [...schedule.pauseRanges]
      : [];

    if (!pauseRanges.length && schedule?.pauseFrom && schedule?.pauseUntil) {
      pauseRanges = [
        {
          from: schedule.pauseFrom,
          until: schedule.pauseUntil,
        },
      ];
    }

    if (pauseMode === "day") {
      if (!pauseDate) {
        setError("Please select the day you want to pause.");
        return;
      }

      from = pauseDate;
      until = pauseDate;
      pauseDates = [...new Set([...pauseDates, pauseDate])];
    }

    if (pauseMode === "week") {
      ({ from, until } = getNextCalendarWeekRange());
    }

    if (pauseMode === "month") {
      ({ from, until } = getNextMonthRange());
    }

    if (pauseMode === "custom") {
      from = pauseRange.from;
      until = pauseRange.until;

      if (!from || !until || from > until) {
        setError("Please select a valid pause date range.");
        return;
      }
    }

    const lockedInRange = deliveries.find((item) =>
      item.scheduleId === delivery.scheduleId &&
      item.deliveryDate &&
      item.deliveryDate >= from &&
      (!until || item.deliveryDate <= until) &&
      !["Delivered", "Cancelled", "Skipped"].includes(item.status) &&
      !isOrderModifiable(item),
    );

    if (lockedInRange) {
      setError(
        `The delivery on ${lockedInRange.deliveryDate} is inside the 24-hour modification window. Pause changes are not allowed for that delivery.`,
      );
      return;
    }

    setSaving(true);
    setError("");

    try {
      pauseRanges = [...pauseRanges, { from, until }];

      await updateSchedule(delivery.scheduleId, {
        status: "active",
        pauseFrom: null,
        pauseUntil: null,
        pauseRanges,
        pauseDates,
      });

      await updateOrdersForScheduleInStore(
        delivery.scheduleId,
        "Skipped",
        from,
        until,
      );

      markLocalOrders(from, until, "Skipped");

      setSchedule((previous) => ({
        ...(previous || {}),
        status: "active",
        pauseFrom: null,
        pauseUntil: null,
        pauseRanges,
        pauseDates,
      }));

      setPauseDate("");
      setPauseRange({ from: "", until: "" });
    } catch (err) {
      setError(err?.message || "Unable to pause this schedule.");
    } finally {
      setSaving(false);
    }
  };

  const getUpcomingOrders = () =>
    deliveries
      .filter((item) =>
        item.scheduleId === delivery?.scheduleId &&
        item.deliveryDate &&
        item.deliveryDate >= toDateKey() &&
        !["Delivered", "Cancelled", "Skipped"].includes(item.status),
      )
      .sort((a, b) => String(a.deliveryDate).localeCompare(String(b.deliveryDate)));

  const skipNextDelivery = async () => {
    if (!delivery?.scheduleId) return;

    const nextOrder = getUpcomingOrders()[0];
    const dateToSkip = nextOrder?.deliveryDate || nextDate || delivery.deliveryDate;
    const skipCandidate = nextOrder || {
      deliveryDate: dateToSkip,
      timeSlot: currentSchedule?.deliveryTimeSlot || delivery?.timeSlot,
    };

    if (!dateToSkip) {
      setError("There is no upcoming delivery to skip.");
      return;
    }

    if (!isOrderModifiable(skipCandidate)) {
      setError("This delivery is inside the 24-hour modification window and cannot be skipped.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const pauseDates = Array.isArray(schedule?.pauseDates)
        ? [...new Set([...schedule.pauseDates, dateToSkip])]
        : [dateToSkip];

      // Update the actual existing order when it exists. Do not create a new
      // order just to represent a skipped delivery.
      if (nextOrder?.id) {
        await updateOrderStatus(nextOrder.id, "Skipped");
      }

      await updateSchedule(delivery.scheduleId, {
        status: "active",
        pauseDates,
      });

      if (nextOrder?.id) {
        setDeliveries((previous) =>
          previous.map((item) =>
            item.id === nextOrder.id ? { ...item, status: "Skipped" } : item,
          ),
        );
      } else {
        markLocalOrders(dateToSkip, dateToSkip, "Skipped");
      }

      setSchedule((previous) => ({
        ...(previous || {}),
        status: "active",
        pauseDates,
      }));
    } catch (err) {
      setError(err?.message || "Unable to skip the next delivery.");
    } finally {
      setSaving(false);
    }
  };

  const resumeSchedule = async () => {
    if (!delivery?.scheduleId) return;

    setSaving(true);
    setError("");

    try {
      await updateSchedule(delivery.scheduleId, {
        status: "active",
        pauseFrom: null,
        pauseUntil: null,
        pauseRanges: [],
        pauseDates: [],
      });

      setSchedule((previous) => ({
        ...(previous || {}),
        status: "active",
        pauseFrom: null,
        pauseUntil: null,
        pauseRanges: [],
        pauseDates: [],
      }));
    } catch (err) {
      setError(err?.message || "Unable to resume this schedule.");
    } finally {
      setSaving(false);
    }
  };

  const stopSchedule = async () => {
    if (!delivery?.scheduleId) return;

    const nextOrder = getUpcomingOrders()[0];
    if (nextOrder && !isOrderModifiable(nextOrder)) {
      setError("The next delivery is inside the 24-hour modification window. The subscription cannot be changed until that delivery is outside the locked window.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await updateSchedule(delivery.scheduleId, {
        status: "cancelled",
        pauseFrom: null,
        pauseUntil: null,
        pauseRanges: [],
        pauseDates: [],
      });

      await updateOrdersForScheduleInStore(
        delivery.scheduleId,
        "Cancelled",
        toDateKey(),
        null,
      );

      markLocalOrders(toDateKey(), null, "Cancelled");

      setSchedule((previous) => ({
        ...(previous || {}),
        status: "cancelled",
        pauseFrom: null,
        pauseUntil: null,
        pauseRanges: [],
        pauseDates: [],
      }));
    } catch (err) {
      setError(err?.message || "Unable to stop this schedule.");
    } finally {
      setSaving(false);
    }
  };

  if (!delivery) {
    return (
      <div className="page-wrap narrow container-xl">
        <PageHeader
          title="Schedule not found"
          copy="This delivery is no longer available."
        />
        <Link to="/deliveries" className="btn btn-brand w-100">
          Back to deliveries
        </Link>
      </div>
    );
  }

  return (
    <div className="page-wrap narrow container-xl">
      <PageHeader
        eyebrow="Recurring schedule"
        title="Manage subscription"
        copy="Update the recurring schedule, pause selected dates, skip a delivery, or stop future deliveries. Changes to deliveries are allowed only more than 24 hours in advance."
      />

      <div className="subscription-hero">
        <span className="service-icon">
          <i
            className={`fa-solid ${
              product?.type === "fruit_basket"
                ? "fa-basket-shopping"
                : "fa-glass-water"
            }`}
          />
        </span>

        <div>
          <StatusBadge
            status={
              isCancelled ? "Cancelled" : pausedToday ? "Paused" : "Active"
            }
          />
          <h2>{product?.name || "Scheduled delivery"}</h2>
          <p>
            {displayDays} ·{" "}
            {currentSchedule?.deliveryTimeSlot ||
              delivery?.timeSlot ||
              timeSlots[2]}
          </p>
        </div>
      </div>

      <div className="schedule-edit-notice mt-3">
        <i className="fa-solid fa-circle-info" />
        <div><strong>24-hour modification policy</strong><span>Any delivery that starts within the next 24 hours is locked. “Change this delivery” affects only the selected order. “Change recurring schedule” updates future editable deliveries. A recurring schedule has one common delivery window; if Monday and Thursday need different times, use separate schedules.</span></div>
      </div>

      {loading && (
        <div className="admin-loading mt-3">
          <i className="fa-solid fa-spinner fa-spin" /> Loading schedule…
        </div>
      )}

      {error && <div className="alert alert-danger mt-3">{error}</div>}

      {!isCancelled && (
        <div className="action-list">
          <Link
            to={`/schedule/${product?.type || delivery.type}/${product?.id || delivery.productId}?editOrderId=${encodeURIComponent(delivery.id)}&editMode=order`}
          >
            <i className="fa-regular fa-calendar-days" />
            Change this delivery
            <i className="fa-solid fa-chevron-right" />
          </Link>

          <Link
            to={`/schedule/${product?.type || delivery.type}/${product?.id || delivery.productId}?editScheduleId=${encodeURIComponent(delivery.scheduleId)}&editOrderId=${encodeURIComponent(delivery.id)}`}
          >
            <i className="fa-regular fa-calendar" />
            Change recurring schedule
            <i className="fa-solid fa-chevron-right" />
          </Link>

          <Link
            to={`/schedule/${product?.type || delivery.type}/${product?.id || delivery.productId}?editScheduleId=${encodeURIComponent(delivery.scheduleId)}&editOrderId=${encodeURIComponent(delivery.id)}`}
          >
            <i className="fa-regular fa-clock" />
            Change delivery time
            <i className="fa-solid fa-chevron-right" />
          </Link>

          <Link
            to={`/schedule/${product?.type || delivery.type}/${product?.id || delivery.productId}?editScheduleId=${encodeURIComponent(delivery.scheduleId)}&editOrderId=${encodeURIComponent(delivery.id)}`}
          >
            <i className="fa-solid fa-location-dot" />
            Change address
            <i className="fa-solid fa-chevron-right" />
          </Link>

          <button
            type="button"
            data-bs-toggle="modal"
            data-bs-target="#pauseModal"
          >
            <i className="fa-solid fa-pause" />
            Pause deliveries
            <i className="fa-solid fa-chevron-right" />
          </button>

          <button type="button" onClick={skipNextDelivery} disabled={saving}>
            <i className="fa-solid fa-forward-step" />
            Skip next delivery
            <i className="fa-solid fa-chevron-right" />
          </button>

          <button
            type="button"
            className="text-danger"
            data-bs-toggle="modal"
            data-bs-target="#cancelModal"
          >
            <i className="fa-regular fa-circle-xmark" />
            Stop subscription
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      )}

      {pausedToday && !isCancelled && (
        <button
          type="button"
          className="btn btn-brand w-100 mt-3"
          onClick={resumeSchedule}
          disabled={saving}
        >
          {saving ? "Updating…" : "Resume all deliveries"}
        </button>
      )}

      {/* ================= PAUSE ================= */}

      <div className="modal fade" id="pauseModal" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body p-4">
              <h2>Pause deliveries</h2>
              <p className="text-secondary">
                Choose exactly when FreshLoop should skip this schedule.
              </p>

              <div className="choice-grid mt-3">
                {[
                  ["day", "One day"],
                  ["week", "Next week"],
                  ["month", "Next month"],
                  ["custom", "Date range"],
                ].map(([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    className={pauseMode === value ? "selected" : ""}
                    onClick={() => setPauseMode(value)}
                  >
                    {label}
                    {pauseMode === value && (
                      <i className="fa-solid fa-circle-check check" />
                    )}
                  </button>
                ))}
              </div>

              {pauseMode === "day" && (
                <div className="mt-3">
                  <label className="form-label">Select the day</label>
                  <input
                    type="date"
                    className="form-control"
                    min={toDateKey()}
                    value={pauseDate}
                    onChange={(event) => setPauseDate(event.target.value)}
                  />
                </div>
              )}

              {pauseMode === "custom" && (
                <div className="row g-3 mt-1">
                  <div className="col-6">
                    <label className="form-label">From</label>
                    <input
                      type="date"
                      className="form-control"
                      min={toDateKey()}
                      value={pauseRange.from}
                      onChange={(event) =>
                        setPauseRange((current) => ({
                          ...current,
                          from: event.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-6">
                    <label className="form-label">Until</label>
                    <input
                      type="date"
                      className="form-control"
                      min={pauseRange.from || toDateKey()}
                      value={pauseRange.until}
                      onChange={(event) =>
                        setPauseRange((current) => ({
                          ...current,
                          until: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              {pauseMode === "week" && (
                <p className="small text-secondary mt-3 mb-0">
                  This pauses the next Monday–Sunday calendar week.
                </p>
              )}

              {pauseMode === "month" && (
                <p className="small text-secondary mt-3 mb-0">
                  This pauses the entire next calendar month.
                </p>
              )}

              <PrimaryButton
                className="w-100 mt-4"
                onClick={savePause}
                disabled={saving}
              >
                {saving ? "Saving…" : "Pause deliveries"}
              </PrimaryButton>

              <button
                type="button"
                className="btn btn-soft w-100 mt-2"
                data-bs-dismiss="modal"
              >
                Keep schedule
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ================= STOP ================= */}

      <div className="modal fade" id="cancelModal" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body p-4 text-center">
              <h2>Stop this subscription?</h2>
              <p>
                This will stop all future deliveries. Existing delivered orders
                will not be changed.
              </p>

              <button
                type="button"
                className="btn btn-danger w-100"
                data-bs-dismiss="modal"
                onClick={stopSchedule}
                disabled={saving}
              >
                {saving ? "Stopping…" : "Stop subscription"}
              </button>

              <button
                type="button"
                className="btn btn-soft w-100 mt-2"
                data-bs-dismiss="modal"
              >
                Keep schedule
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Subscription;
